import { z } from '@customerio/hydra-validation';

const featureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(z.unknown()),
});
const lineFeatureSchema = z.object({ type: z.literal('Feature') });
const polygonFeatureSchema = z.object({ geometry: z.object({ type: z.enum(['Polygon', 'MultiPolygon']) }) });

type ParsedGeoJSON<T> = { success: true; features: T[] } | { success: false; error: string };

export function parseGeosetGeoJSON<T>(
  text: string,
  parseFeature: (input: unknown, index: number) => T,
): ParsedGeoJSON<T> {
  const data = text.replace(/^\uFEFF/, '');
  let document: unknown;
  try {
    document = JSON.parse(data);
  } catch {
    // Exports contain one Feature per line, not one JSON document.
  }
  if (document !== undefined) {
    const collection = featureCollectionSchema.safeParse(document);
    if (collection.success) {
      return {
        success: true,
        features: collection.data.features.map((feature, index) => parseFeature(feature, index + 1)),
      };
    }
    if (lineFeatureSchema.safeParse(document).success && !data.trim().includes('\n')) {
      return { success: true, features: [parseFeature(document, 1)] };
    }
    return {
      success: false,
      error:
        "This file isn't a supported GeoJSON format. Use a FeatureCollection with a features array, or one Feature per line.",
    };
  }

  return parseGeosetGeoJSONLines(data, parseFeature);
}

function parseGeosetGeoJSONLines<T>(
  data: string,
  parseFeature: (input: unknown, index: number) => T,
): ParsedGeoJSON<T> {
  const features: T[] = [];
  let offset = 0;
  let lineNumber = 0;
  while (offset < data.length) {
    const newline = data.indexOf('\n', offset);
    const end = newline === -1 ? data.length : newline;
    const line = data.slice(offset, end);
    offset = end + 1;
    lineNumber++;
    if (line.trim() === '') continue;
    let input: unknown;
    try {
      input = JSON.parse(line);
    } catch {
      return {
        success: false,
        error:
          features.length === 0
            ? "This file isn't valid JSON. Export it again or check for a truncated download."
            : `Line ${lineNumber} isn't valid JSON. Export the file again or check that line.`,
      };
    }
    const feature = lineFeatureSchema.safeParse(input);
    if (!feature.success) {
      return {
        success: false,
        error: `Line ${lineNumber} is not a GeoJSON Feature. Use a FeatureCollection or one Feature per line.`,
      };
    }
    features.push(parseFeature(input, features.length + 1));
  }
  if (features.length === 0) {
    return {
      success: false,
      error: 'This file has no GeoJSON features. Add at least one feature and upload it again.',
    };
  }
  return { success: true, features };
}

// Preview and upload use polygon intent, including malformed coordinates.
export function hasPolygonGeometry(feature: unknown): boolean {
  return polygonFeatureSchema.safeParse(feature).success;
}

export function isGeosetGeoJSON(filename: string, text: string): boolean {
  return /\.(geojson|geojsonl|json)$/i.test(filename) || text.trimStart().startsWith('{');
}

// CSV cannot represent polygons.
export function allowsPartialGeosetFile(filename: string, text: string): boolean {
  const trimmed = text.trimStart();
  if (!isGeosetGeoJSON(filename, trimmed)) return true;
  const parsed = parseGeosetGeoJSON(trimmed, hasPolygonGeometry);
  return parsed.success && !parsed.features.some((isPolygon) => isPolygon);
}
